import { useMemo, useState } from 'react';

import { EU_TARIFF_TYPE_TEXTS } from '@/components/App/NewOrder/constants';
import { BANKS_ICONS_NAMES, DictTypes } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useAppSelector } from '@/store';

import { EuBankV2Status } from '../../../../../../Shared/constants/banks-statuses';
import type { TEurasV2BankOffer } from '../../../types/euras-v2.types';
import useAdditionalHelpers from './useAdditionalHelpers';
import useAlternatives from './useAlternatives';

type IProps = {
  bankOffer: TEurasV2BankOffer;
};

const blueColoredStatuses = [
  EuBankV2Status.WaitingOfFinancing,
  EuBankV2Status.MobileClient,
  EuBankV2Status.OnRework,
  EuBankV2Status.VehicleRegistration,
  EuBankV2Status.WaitingForDocsConfirmation
];

const useFormulateOfferItem = ({ bankOffer: initialBankOffer }: IProps) => {
  const [windowWidth] = useWindowSize();
  const isMobile = windowWidth <= 860;

  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const eurasianTariffType = orderApp?.productInfo.product
    .eurasianTariffType as keyof typeof EU_TARIFF_TYPE_TEXTS;

  const [offerDetailsModal, setOfferDetailsModal] = useState<boolean | string>(
    false
  );

  const { alternatives } = useAlternatives({ bankOffer: initialBankOffer });
  const bankOffer: TEurasV2BankOffer = useMemo(() => {
    const product =
      orderApp?.productInfo.product.stocksBonds?.find(
        (prod) => prod.bank_name === initialBankOffer.bank.name
      )?.label ||
      EU_TARIFF_TYPE_TEXTS[eurasianTariffType] ||
      'Стандарт';

    if (!alternatives || alternatives.length === 0) {
      return {
        ...initialBankOffer,
        product
      };
    }

    // По 2 версии евраза не бывает основного предложения, всегда только альтернативы
    const selectedAlternative =
      alternatives.find(
        (item) => item.extUuid === initialBankOffer.offeredUuid
      ) || alternatives[0];
    const filteredAlternatives = alternatives.filter(
      (item) => item.extUuid !== selectedAlternative?.extUuid
    );

    let result = {
      ...initialBankOffer,
      alternatives: filteredAlternatives,
      product
    };
    if (selectedAlternative) {
      result = {
        ...result,
        rate: selectedAlternative.rate.toString(),
        extId: selectedAlternative.extUuid,
        initialPayment: selectedAlternative.initialPayment,
        loanAmount: selectedAlternative.loanAmount,
        loanPeriod: selectedAlternative.loanPeriod.toString(),
        monthlyPayment: selectedAlternative.monthlyPayment,
        firstMonthlyPayment: selectedAlternative.firstMonthlyPayment,
        additionalConditions: selectedAlternative.additionalConditions,
        product:
          selectedAlternative.product ||
          EU_TARIFF_TYPE_TEXTS[eurasianTariffType] ||
          product,
        // productInfo может не иметь current_value когда productInfo изначально
        // undefined, но additionalProducts есть — это допустимый runtime-случай
        productInfo:
          typeof result.productInfo === 'object' ||
          (typeof result.productInfo === 'undefined' &&
            selectedAlternative.additionalProducts)
            ? ({
                ...(result.productInfo || {}),
                additionalProducts: selectedAlternative.additionalProducts
              } as TEurasV2BankOffer['productInfo'])
            : result.productInfo
      };
    }

    return result as TEurasV2BankOffer;
  }, [
    initialBankOffer,
    alternatives,
    orderApp?.productInfo.product.stocksBonds,
    eurasianTariffType
  ]);

  const { additionalProducts, getDescription } = useAdditionalHelpers({
    bankOffer,
    offerDetailsModal
  });

  const bankDict =
    dicts?.[DictTypes.Banks]?.getBy.name[initialBankOffer.bank.name];
  const bankIconName =
    BANKS_ICONS_NAMES[
      initialBankOffer.bank.name as keyof typeof BANKS_ICONS_NAMES
    ];

  return {
    additionalProducts,
    bankOffer,
    getDescription,
    headerIcon: bankDict?.icon || bankIconName?.icon,
    headerTitle: bankDict?.fullname || bankIconName?.name,
    isBlueColoredStatus: blueColoredStatuses.includes(
      initialBankOffer.status.code as EuBankV2Status
    ),
    isMobile,
    offerDetailsModal,
    setOfferDetailsModal
  };
};

export default useFormulateOfferItem;
